import { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GUESTHOUSE_API_BASE = API_ROOT.endsWith("/api")
  ? `${API_ROOT}/guesthouse`
  : `${API_ROOT}/api/guesthouse`;
const STATUS_OPTIONS = [
  "Pending",
  "Booked",
  "Checked-In",
  "Checked-Out",
  "Cancelled",
  "Request Payment",
  "Not Available"
];

const ACTION_OPTIONS = ["Booked", "Cancelled", "Not Available", "Request Payment"];

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const toInputDate = (dateObj) => dateObj.toISOString().slice(0, 10);

// Mirrors the guest notification bell toggle in Header.jsx (Module 10 only, browser-local).
const isGhNotifyEnabled = () => {
  try {
    return localStorage.getItem("gh_notify_enabled") !== "0";
  } catch {
    return true;
  }
};

export default function GuesthousePendingBookings() {
    // --- FORM for Pending Bookings Admin Panel (like Employee Panel) ---
    // This form is rendered at the top, before the table, as the first step.
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const today = useMemo(() => new Date(), []);

  const [guestHouses, setGuestHouses] = useState([]);
  const [guestHouseID, setGuestHouseID] = useState("");
  const [rStatus, setRStatus] = useState("Pending");
  const [fromDate, setFromDate] = useState(toInputDate(today));
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [tariffs, setTariffs] = useState({});
  const [tariffIDs, setTariffIDs] = useState({});
  const [gsts, setGsts] = useState({});
  const [discounts, setDiscounts] = useState({});
  const [discountAmounts, setDiscountAmounts] = useState({});
  const [roomSelections, setRoomSelections] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [tariffOptionsMap, setTariffOptionsMap] = useState({});
  const [tariffKeys, setTariffKeys] = useState({});

  useEffect(() => {
    const fetchGuestHouses = async () => {
      try {
        const moduleId = user?.moduleId;
        const res = await fetch(`${GUESTHOUSE_API_BASE}/options?moduleId=${encodeURIComponent(moduleId || "")}`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch guest houses");
        }

        setGuestHouses(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length) {
          setGuestHouseID(String(data[0].GuestHouseID));
        }
      } catch (err) {
        setError(err.message || "Failed to fetch guest houses");
      }
    };

    fetchGuestHouses();
  }, [user?.moduleId]);

  useEffect(() => {
    if (!guestHouseID) return;
    if (tariffOptionsMap[guestHouseID] !== undefined) return;
    const fetchTariffOptions = async () => {
      try {
        const q = new URLSearchParams({ moduleId: String(user?.moduleId || ""), guestHouseID });
        const res = await fetch(`${GUESTHOUSE_API_BASE}/tariff/booking-options?${q}`);
        const data = await res.json();
        if (res.ok) setTariffOptionsMap(prev => ({ ...prev, [guestHouseID]: data.data || [] }));
      } catch {}
    };
    fetchTariffOptions();
  }, [guestHouseID]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTable = async () => {
    setError("");
    setSuccessMsg("");

    if (!guestHouseID) {
      setError("Please select Guest House.");
      return;
    }

    try {
      setLoading(true);
      const q = new URLSearchParams({
        moduleId: String(user?.moduleId || ""),
        guestHouseID: String(guestHouseID),
        rStatus,
        fromDate
      });

      const res = await fetch(`${GUESTHOUSE_API_BASE}/pending-bookings?${q.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch booking table");
      }

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch booking table");
    } finally {
      setLoading(false);
    }
  };

  const toVisitorRef = (bookingID, vIdx, v) => {
    if (vIdx === 0) return String(bookingID);
    if (v?.BookingRef != null) return `${bookingID}${v.BookingRef}`;
    return String(bookingID);
  };

  const getVisitorKey = (bookingID, visitorID, vIdx) => {
    if (vIdx === 0) return `${bookingID}-MAIN`;
    return `${bookingID}-V-${visitorID || vIdx}`;
  };

  const handleAction = async ({ row, v, vIdx, status }) => {
    const visitorKey = getVisitorKey(row.BookingID, v?.VisitorID, vIdx);
    const roomID = roomSelections[visitorKey] || "";

    if (status === "Cancelled") {
      const ref = toVisitorRef(row.BookingID, vIdx, v);
      if (!window.confirm(`Cancel booking ${ref}? This cannot be undone.`)) return;
    }

    if ((status === "Booked" || status === "Request Payment") && !roomID) {
      setError(`Please select room for ${toVisitorRef(row.BookingID, vIdx, v)} before setting "${status}".`);
      return;
    }

    if ((status === "Booked" || status === "Request Payment") && !tariffs[visitorKey]) {
      setError(`Please select tariff for ${toVisitorRef(row.BookingID, vIdx, v)} before setting "${status}".`);
      return;
    }

    const baseAmt = parseFloat(tariffs[visitorKey]) || 0;
    const discPct = parseFloat(discounts[visitorKey]) || 0;
    const calculatedDiscAmt = Math.round((baseAmt * discPct) / 100);
    const discAmt = parseFloat(discountAmounts[visitorKey] ?? String(calculatedDiscAmt)) || 0;

    try {
      setError("");
      setActionLoading(prev => ({ ...prev, [visitorKey]: true }));

      const q = new URLSearchParams({ moduleId: String(user?.moduleId || "") });
      const res = await fetch(`${GUESTHOUSE_API_BASE}/pending-bookings/action?${q.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingID: row.BookingID,
          visitorID: vIdx === 0 ? null : (v?.VisitorID != null ? v.VisitorID : null),
          status,
          roomID: (status === "Booked" || status === "Request Payment") ? parseInt(roomID, 10) : null,
          tariff: tariffs[visitorKey] || null,
          tariffID: tariffIDs[visitorKey] || null,
          gst: parseFloat(gsts[visitorKey] ?? "5"),
          discountAmount: discAmt,
          notify: isGhNotifyEnabled()
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to update status");
      }

      setSuccessMsg(data.message || "Status updated successfully");
      await fetchTable();
    } catch (err) {
      setError(err.message || "Failed to update status");
    } finally {
      setActionLoading(prev => ({ ...prev, [visitorKey]: false }));
    }
  };


  return (
    <section className="container-fluid">
      <h3 className="mb-3">Booking Status Admin</h3>

      {/* FORM: Guest House, Status, From Date, View button */}
      <form onSubmit={e => { e.preventDefault(); fetchTable(); }} className="mb-3">
        <table style={{ borderCollapse: 'collapse', width: 'auto', border: '0' }}>
          <tbody>
            <tr style={{ border: '0' }}>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">Guest House</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">Status</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">From Date</label></td>
              <td style={{ border: '0', padding: '0 0 4px 0' }}><label className="form-label fw-semibold mb-0">View</label></td>
            </tr>
            <tr style={{ border: '0' }}>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <select
                  className="form-select form-select-sm"
                  value={guestHouseID}
                  onChange={(e) => setGuestHouseID(e.target.value)}
                  required
                >
                  <option value="">Select Guest House</option>
                  {guestHouses.map((g) => (
                    <option key={g.GuestHouseID} value={g.GuestHouseID}>
                      {g.GuestHouse}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <select className="form-select form-select-sm" value={rStatus} onChange={(e) => setRStatus(e.target.value)} required>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <input className="form-control form-control-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} required />
              </td>
              <td style={{ border: '0', padding: '0', verticalAlign: 'bottom' }}>
                <button className="btn btn-primary btn-sm" type="submit" disabled={loading} style={{ padding: '5px 20px' }}>
                  {loading ? "Loading..." : "View"}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {error      && <div className="alert alert-danger">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      <div className="table-responsive">
        <table className="table table-bordered align-middle" style={{ borderCollapse: "collapse" }}>
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>SN</th>
              <th style={{ minWidth: '90px' }}>ApDt/BID</th>
              <th style={{ minWidth: '220px' }}>Visitors</th>
              <th style={{ whiteSpace: "nowrap" }}>RType</th>
              <th style={{ whiteSpace: "nowrap" }}>Purpose</th>
              <th style={{ whiteSpace: "nowrap" }}>ChkIn</th>
              <th style={{ whiteSpace: "nowrap" }}>ChkOut</th>
              <th style={{ minWidth: '220px' }}>BookedBy</th>
              <th style={{ whiteSpace: "nowrap" }}>Status</th>
              <th style={{ whiteSpace: "nowrap", minWidth: '130px' }}>Vacant Rooms</th>
              <th style={{ whiteSpace: "nowrap", minWidth: '180px' }}>Tariff</th>
              <th style={{ whiteSpace: "nowrap", minWidth: '75px' }}>GST</th>
              <th style={{ whiteSpace: "nowrap" }}>Dis%</th>
              <th style={{ whiteSpace: "nowrap" }}>DisAmt</th>
              <th style={{ whiteSpace: "nowrap", minWidth: '130px' }}>Action</th>
              <th style={{ minWidth: '220px' }}>Remarks</th>
              <th style={{ whiteSpace: "nowrap", width: '1%' }}>Print</th>
              <th style={{ whiteSpace: "nowrap", width: '1%' }}>View</th>
              {rStatus === "Request Payment" && <th style={{ whiteSpace: "nowrap" }}>Pmt Requested At</th>}
            </tr>
          </thead>
          <tbody>
            {!rows.length && (
              <tr>
                <td colSpan={rStatus === "Request Payment" ? 19 : 18} className="text-center text-muted">No records found.</td>
              </tr>
            )}
            {rows.map((row, idx) => {
              const ROOM_TYPE_LABELS = { R: "Room", S: "Suite", C: "Conference Hall", D: "Dining Hall", E: "Executive Dining Hall", A: "Conf+Dining+Exec Hall", V: "VC Suite", H: "Seminar Hall" };
              const roomType = ROOM_TYPE_LABELS[row.RoomType] || row.RoomType || "";
              const visitType = row.TypeOfVisit === "O" ? "Official" : "Personal";

              const mainVisitor = {
                VisitorName: row.VisitorName || "",
                Mobile: row.Mobile || "",
                Relationship: row.Relationship || "",
                Status: row.Status || ""
              };
              const extraVisitors = Array.isArray(row.visitors) ? row.visitors : [];
              const allVisitors = [mainVisitor, ...extraVisitors];
              const rowSpan = allVisitors.length;

              const nw = { whiteSpace: "nowrap" };

              return allVisitors.map((v, vIdx) => {
                const visitorKey = getVisitorKey(row.BookingID, v?.VisitorID, vIdx);
                const baseAmt = parseFloat(tariffs[visitorKey]) || 0;
                const discPct = parseFloat(discounts[visitorKey]) || 0;
                const calculatedDiscAmt = Math.round((baseAmt * discPct) / 100);
                const discAmt = discountAmounts[visitorKey] ?? String(calculatedDiscAmt);
                const visitorRef = toVisitorRef(row.BookingID, vIdx, v);

                const vacantRoomSelect = (
                  Array.isArray(row.vacantRooms) && row.vacantRooms.length > 0 ? (
                    <select
                      className="form-select form-select-sm"
                      value={roomSelections[visitorKey] || ""}
                      onChange={(e) => setRoomSelections(prev => ({ ...prev, [visitorKey]: e.target.value }))}
                    >
                      <option value="">Select Room</option>
                      {row.vacantRooms.map((r) => (
                        <option key={r.RoomID} value={r.RoomID}>{r.Room}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-danger">No Room</span>
                  )
                );

                const visitorLabel = (() => {
                  let d = v.VisitorName || "";
                  if (v.Mobile || v.Relationship) {
                    d += ` (${v.Mobile || "-"}${v.Relationship ? ", " + v.Relationship : ""})`;
                  }
                  return `${visitorRef} - ${d}`;
                })();

                return (
                  <tr key={`${row.BookingID}-${idx}-${vIdx}`} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8f9fa" }}>
                    {vIdx === 0 && (
                      <>
                        <td rowSpan={rowSpan} style={{ ...nw, textAlign: "center" }}>{idx + 1}</td>
                        <td rowSpan={rowSpan} style={{ minWidth: '90px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{`${formatDate(row.BookingDate)} (${row.BookingID})`}</td>
                      </>
                    )}
                    <td style={{ minWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{visitorLabel}</td>
                    {vIdx === 0 && (
                      <>
                        <td rowSpan={rowSpan} style={nw}>{roomType}</td>
                        <td rowSpan={rowSpan} style={nw}>{`${visitType}, ${row.PurposeOfVisit || ""}`}</td>
                        <td rowSpan={rowSpan} style={nw}>{formatDate(row.FromDate)}</td>
                        <td rowSpan={rowSpan} style={nw}>{formatDate(row.ToDate)}</td>
                        <td rowSpan={rowSpan} style={{ minWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{row.EmpName}</td>
                      </>
                    )}
                    <td style={nw}>{v.Status || ""}</td>
                    <td style={{ ...nw, minWidth: '130px' }}>{vacantRoomSelect}</td>
                    <td style={{ ...nw, minWidth: '180px' }}>
                      <select
                        className="form-select form-select-sm"
                        value={tariffKeys[visitorKey] || ""}
                        onChange={e => {
                          const key = e.target.value;
                          setTariffKeys(prev => ({ ...prev, [visitorKey]: key }));
                          const matched = (tariffOptionsMap[guestHouseID] || []).find(
                            o => `${o.TariffCategoryID}_${o.RoomType}` === key
                          );
                          const newAmt = matched ? String(matched.Amount) : "";
                          setTariffs(prev => ({ ...prev, [visitorKey]: newAmt }));
                          setTariffIDs(prev => ({ ...prev, [visitorKey]: matched ? matched.TariffID : "" }));
                          const newBase = parseFloat(newAmt) || 0;
                          const pct = parseFloat(discounts[visitorKey]) || 0;
                          setDiscountAmounts(prev => ({ ...prev, [visitorKey]: String(Math.round((newBase * pct) / 100)) }));
                        }}
                      >
                        <option value="" disabled>Tariff</option>
                        {(tariffOptionsMap[guestHouseID] || []).map(opt => (
                          <option key={`${opt.TariffCategoryID}_${opt.RoomType}`} value={`${opt.TariffCategoryID}_${opt.RoomType}`}>
                            {opt.TariffCategory} {opt.RoomType} ({opt.Amount})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ ...nw, minWidth: '75px' }}>
                      <select
                        className="form-select form-select-sm"
                        value={gsts[visitorKey] ?? "5"}
                        onChange={e => setGsts(prev => ({ ...prev, [visitorKey]: e.target.value }))}
                      >
                        <option value="0">0%</option>
                        <option value="5">5%</option>
                        <option value="12">12%</option>
                        <option value="18">18%</option>
                      </select>
                    </td>
                    <td style={nw}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: "80px" }}
                        value={discounts[visitorKey] ?? "0"}
                        min="0"
                        max="100"
                        onChange={e => {
                          const nextDiscount = e.target.value;
                          setDiscounts(prev => ({ ...prev, [visitorKey]: nextDiscount }));
                          const nextPct = parseFloat(nextDiscount) || 0;
                          setDiscountAmounts(prev => ({ ...prev, [visitorKey]: String(Math.round((baseAmt * nextPct) / 100)) }));
                        }}
                      />
                    </td>
                    <td style={nw}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: "80px" }}
                        value={discAmt}
                        min="0"
                        onChange={e => setDiscountAmounts(prev => ({ ...prev, [visitorKey]: e.target.value }))}
                      />
                    </td>
                    <td style={{ ...nw, minWidth: '130px' }}>
                      <select
                        className="form-select form-select-sm"
                        value=""
                        disabled={!!actionLoading[visitorKey]}
                        onChange={e => {
                          const nextStatus = e.target.value;
                          if (!nextStatus) return;
                          handleAction({ row, v, vIdx, status: nextStatus });
                        }}
                      >
                        <option value="">Action</option>
                        {ACTION_OPTIONS.map((actionValue) => (
                          <option key={actionValue} value={actionValue}>{actionValue}</option>
                        ))}
                      </select>
                    </td>
                    {vIdx === 0 && (
                      <>
                        <td rowSpan={rowSpan} style={{ minWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{row.Remarks}</td>
                        <td rowSpan={rowSpan} style={nw}>
                          <a
                            href={`${GUESTHOUSE_API_BASE}/booking/print/${encodeURIComponent(row.BookingID)}?moduleId=10`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Form
                          </a>
                        </td>
                        <td rowSpan={rowSpan} style={nw}>
                          {row.SupportDoc ? (() => {
                            const normalized = String(row.SupportDoc || "").trim().replace(/\\/g, "/");
                            const fileName = normalized.split("/").pop();
                            return fileName ? (
                              <a
                                href={`${GUESTHOUSE_API_BASE}/ghbooking_request/${encodeURIComponent(fileName)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            ) : <span className="text-muted">—</span>;
                          })() : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        {rStatus === "Request Payment" && (
                          <td rowSpan={rowSpan} style={{ whiteSpace: "nowrap" }}>
                            {row.PaymentRequestedAt
                              ? new Date(row.PaymentRequestedAt).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
